import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { SEARCH_LIMITS } from '../api/client'
import { QueryError } from '../components/QueryError'
import { StatusMessage } from '../components/StatusMessage'
import { useSearchQuery, useStatesQuery } from '../hooks/useDisclosureQueries'
import { supportsDateInput } from '../lib/supportsDateInput'
import {
  NOT_AVAILABLE,
  adTypeLabel,
  formatDate,
  isIsoDate,
  isoDateTime,
  positionLabel,
} from '../lib/format'
import type { SearchParams } from '../types'

/** Comfortably above the 300ms floor the API doc asks for. */
const DEBOUNCE_MS = 400

const PER_PAGE = SEARCH_LIMITS.defaultPerPage

/** Probed once at load; the answer cannot change during a session. */
const NATIVE_DATE_INPUT = supportsDateInput()

type DateFieldProps = {
  id: 'from' | 'to'
  label: string
  value: string
  malformed: boolean
  min?: string
  max?: string
  onChange: (value: string) => void
}

/**
 * One date filter.
 *
 * Uses the browser's own date control where there is one — it brings the OS
 * picker, keyboard support, screen-reader semantics and localised ordering for
 * free, none of which a hand-built widget would match. Where there is none, it
 * becomes a text field that states the format it wants instead of a bare box that
 * silently rejects what the user types.
 */
function DateField({ id, label, value, malformed, min, max, onChange }: DateFieldProps) {
  const errorId = `${id}-error`
  const hintId = `${id}-hint`

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type={NATIVE_DATE_INPUT ? 'date' : 'text'}
        /* Fallback only: a numeric keypad on mobile and a shape the browser can
           check, neither of which a native date input needs. */
        inputMode={NATIVE_DATE_INPUT ? undefined : 'numeric'}
        pattern={NATIVE_DATE_INPUT ? undefined : '\\d{4}-\\d{2}-\\d{2}'}
        maxLength={NATIVE_DATE_INPUT ? undefined : 10}
        /* A native picker ignores this; the fallback text field shows it. */
        placeholder="YYYY-MM-DD"
        /* Bounds keep the native picker from offering an inverted range. They mean
           nothing on a text field, so they are not set there. */
        min={NATIVE_DATE_INPUT ? min : undefined}
        max={NATIVE_DATE_INPUT ? max : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        aria-invalid={malformed || undefined}
        aria-describedby={malformed ? errorId : NATIVE_DATE_INPUT ? undefined : hintId}
      />
      {malformed ? (
        <p id={errorId} className="field-error" role="alert">
          Enter the date as YYYY-MM-DD.
        </p>
      ) : NATIVE_DATE_INPUT ? null : (
        <p id={hintId} className="field-hint">
          Format: YYYY-MM-DD
        </p>
      )}
    </div>
  )
}

function parseStates(raw: string | null): string[] {
  if (!raw) return []
  return [...new Set(raw.split(',').map((value) => value.trim().toUpperCase()).filter(Boolean))]
}

function parsePage(raw: string | null): number {
  const page = Number(raw)
  if (!Number.isInteger(page) || page < 1) return 1
  return Math.min(page, SEARCH_LIMITS.maxPage)
}

/** A term shorter than the API minimum is held back rather than sent for a guaranteed 400. */
function usableTerm(value: string): string {
  const trimmed = value.trim()
  return trimmed.length >= SEARCH_LIMITS.minTermLength ? trimmed : ''
}

export function SearchPage() {
  const [urlParams, setUrlParams] = useSearchParams()

  const statesKey = parseStates(urlParams.get('states')).join(',')
  // Memoised so it is referentially stable and safe to use as a query key input.
  const selectedStates = useMemo(() => (statesKey ? statesKey.split(',') : []), [statesKey])
  const purchaserParam = urlParams.get('purchaser') ?? ''
  const candidateParam = urlParams.get('candidate') ?? ''
  const fromParam = urlParams.get('from') ?? ''
  const toParam = urlParams.get('to') ?? ''
  const page = parsePage(urlParams.get('page'))

  // Text inputs are local so typing stays responsive; the URL catches up on a debounce.
  const [purchaserInput, setPurchaserInput] = useState(purchaserParam)
  const [candidateInput, setCandidateInput] = useState(candidateParam)

  /**
   * The date fields are local for the same reason as the text fields, plus one
   * specific to `<input type="date">`: a browser with no native date support
   * (Safari on macOS before 14.1) renders it as a plain text field that fires on
   * every keystroke. Only a complete, real date is allowed through to the URL and
   * the API, so a half-typed value never becomes a request.
   */
  const [fromInput, setFromInput] = useState(fromParam)
  const [toInput, setToInput] = useState(toParam)

  const statesQuery = useStatesQuery()
  const availableStates = statesQuery.data ?? []
  const archiveIsOff = statesQuery.isSuccess && availableStates.length === 0

  const updateParams = (changes: Record<string, string | number | null>) => {
    setUrlParams(
      (current) => {
        const next = new URLSearchParams(current)
        for (const [key, value] of Object.entries(changes)) {
          if (value === null || value === '' || value === undefined) next.delete(key)
          else next.set(key, String(value))
        }
        return next
      },
      { replace: true },
    )
  }

  // Debounced so search-as-you-type never fires a request per keystroke: the API
  // allows 60 requests per minute per IP.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const nextPurchaser = purchaserInput.trim()
      const nextCandidate = candidateInput.trim()
      if (nextPurchaser === purchaserParam && nextCandidate === candidateParam) return
      updateParams({
        purchaser: nextPurchaser || null,
        candidate: nextCandidate || null,
        page: null,
      })
    }, DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
    // updateParams closes over the router setter only; the values above drive this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaserInput, candidateInput, purchaserParam, candidateParam])

  // A hand-edited URL can carry anything, so the values that reach the query are
  // validated rather than trusted.
  const activeFrom = isIsoDate(fromParam) ? fromParam : ''
  const activeTo = isIsoDate(toParam) ? toParam : ''

  const fromMalformed = fromInput !== '' && !isIsoDate(fromInput)
  const toMalformed = toInput !== '' && !isIsoDate(toInput)
  // Lexicographic comparison is exact for YYYY-MM-DD, but only once both are known
  // to be well formed.
  const invalidDateRange = Boolean(activeFrom && activeTo && activeFrom > activeTo)
  const datesUsable = !fromMalformed && !toMalformed && !invalidDateRange

  const handleDateChange = (field: 'from' | 'to', value: string) => {
    if (field === 'from') setFromInput(value)
    else setToInput(value)

    if (value === '' || isIsoDate(value)) {
      updateParams({ [field]: value || null, page: null })
    }
  }

  const searchParams: SearchParams = useMemo(
    () => ({
      states: selectedStates,
      purchaser: usableTerm(purchaserParam) || undefined,
      candidate: usableTerm(candidateParam) || undefined,
      from: activeFrom || undefined,
      to: activeTo || undefined,
      page,
      per_page: PER_PAGE,
    }),
    [selectedStates, purchaserParam, candidateParam, activeFrom, activeTo, page],
  )

  const searchEnabled = selectedStates.length > 0 && !archiveIsOff && datesUsable
  const searchQuery = useSearchQuery(searchParams, searchEnabled)

  const results = searchQuery.data ?? []
  const isFirstLoad = searchQuery.isPending && searchEnabled
  const isRefetching = searchQuery.isFetching && !isFirstLoad
  // The API returns no total count, so a full page is the only signal more may exist.
  const mayHaveNextPage = results.length === PER_PAGE
  const atPageCap = page >= SEARCH_LIMITS.maxPage
  const firstRowNumber = (page - 1) * PER_PAGE + 1

  const toggleState = (isoCode: string) => {
    const next = selectedStates.includes(isoCode)
      ? selectedStates.filter((code) => code !== isoCode)
      : [...selectedStates, isoCode]
    updateParams({ states: next.join(',') || null, page: null })
  }

  const clearFilters = () => {
    setPurchaserInput('')
    setCandidateInput('')
    setFromInput('')
    setToInput('')
    setUrlParams(new URLSearchParams(), { replace: true })
  }

  const hasAnyFilter =
    selectedStates.length > 0 || purchaserInput || candidateInput || fromInput || toInput

  // The current search is carried to the detail page as one opaque parameter so
  // "back to results" survives a reload, without leaking filter names the record
  // endpoint would misread.
  const returnQuery = urlParams.toString()
  const recordHref = (id: number) =>
    returnQuery ? `/records/${id}?from=${encodeURIComponent(returnQuery)}` : `/records/${id}`

  return (
    <div className="stack-lg">
      <header className="page-header">
        <p className="eyebrow">Public records</p>
        <h1>Political advertisement disclosure archive</h1>
        <p className="lede">
          A searchable public record of political advertisements served on this platform, published
          as required by state law. Records remain published for five years, and are updated if
          their disclosure details are later corrected.
        </p>
      </header>

      {statesQuery.isPending ? (
        <StatusMessage title="Loading the archive…">
          <p>Fetching the states with a published archive.</p>
        </StatusMessage>
      ) : statesQuery.isError ? (
        <QueryError
          error={statesQuery.error}
          onRetry={() => void statesQuery.refetch()}
          retrying={statesQuery.isFetching}
        />
      ) : archiveIsOff ? (
        <StatusMessage tone="empty" title="The public archive is not currently available">
          <p>
            No state currently has a published archive on this platform, so there are no records to
            search. This page will list the available states again as soon as publication resumes.
          </p>
        </StatusMessage>
      ) : (
        <>
          <section className="panel" aria-labelledby="search-filters-heading">
            <div className="panel-header">
              <h2 id="search-filters-heading">Search the archive</h2>
              {hasAnyFilter ? (
                <button type="button" className="button button-quiet" onClick={clearFilters}>
                  Clear filters
                </button>
              ) : null}
            </div>

            {/* States drive the whole search: the API scopes figures per state and
                rejects a search without one. */}
            <fieldset className="field-group">
              <legend>
                States <span className="required-hint">(select at least one)</span>
              </legend>
              <div className="state-options">
                {availableStates.map((state) => (
                  <label key={state.iso_code} className="checkbox-pill">
                    <input
                      type="checkbox"
                      checked={selectedStates.includes(state.iso_code)}
                      onChange={() => toggleState(state.iso_code)}
                    />
                    <span>
                      {state.name} <span className="iso">({state.iso_code})</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="filter-grid">
              <div className="field">
                <label htmlFor="purchaser">Purchaser</label>
                <input
                  id="purchaser"
                  type="search"
                  autoComplete="off"
                  maxLength={SEARCH_LIMITS.maxTermLength}
                  value={purchaserInput}
                  onChange={(event) => setPurchaserInput(event.target.value)}
                  placeholder="Purchaser name"
                  aria-describedby="purchaser-hint"
                />
                <p id="purchaser-hint" className="field-hint">
                  {purchaserInput.trim().length === 1
                    ? `Enter at least ${SEARCH_LIMITS.minTermLength} characters.`
                    : 'Partial names match.'}
                </p>
              </div>

              <div className="field">
                <label htmlFor="candidate">Candidate / Subject</label>
                <input
                  id="candidate"
                  type="search"
                  autoComplete="off"
                  maxLength={SEARCH_LIMITS.maxTermLength}
                  value={candidateInput}
                  onChange={(event) => setCandidateInput(event.target.value)}
                  placeholder="Candidate / subject"
                  aria-describedby="candidate-hint"
                />
                <p id="candidate-hint" className="field-hint">
                  {candidateInput.trim().length === 1
                    ? `Enter at least ${SEARCH_LIMITS.minTermLength} characters.`
                    : 'Partial names match.'}
                </p>
              </div>

              <DateField
                id="from"
                label="Published from"
                value={fromInput}
                malformed={fromMalformed}
                max={activeTo || undefined}
                onChange={(value) => handleDateChange('from', value)}
              />

              <DateField
                id="to"
                label="Published to"
                value={toInput}
                malformed={toMalformed}
                min={activeFrom || undefined}
                onChange={(value) => handleDateChange('to', value)}
              />
            </div>

            {invalidDateRange ? (
              <p className="field-error" role="alert">
                The “from” date is after the “to” date. Adjust the range to search.
              </p>
            ) : null}
          </section>

          <section className="stack" aria-labelledby="results-heading">
            <div className="panel-header">
              <h2 id="results-heading">Results</h2>
              <p className="results-count" aria-live="polite">
                {!searchEnabled
                  ? ''
                  : isFirstLoad
                    ? 'Searching…'
                    : searchQuery.isError
                      ? ''
                      : results.length === 0
                        ? 'No records'
                        : `Showing ${results.length} record${results.length === 1 ? '' : 's'} (${firstRowNumber}–${firstRowNumber + results.length - 1}) on page ${page}`}
              </p>
            </div>

            {selectedStates.length === 0 ? (
              <StatusMessage tone="empty" title="Select a state to begin">
                <p>
                  Disclosure figures are reported per state, so every search needs at least one
                  state. Choose one or more above.
                </p>
              </StatusMessage>
            ) : !datesUsable ? (
              <StatusMessage tone="empty" title="Check the date range">
                <p>
                  {invalidDateRange
                    ? 'The “from” date must be on or before the “to” date.'
                    : 'Enter dates as YYYY-MM-DD, or clear them to search without a date range.'}
                </p>
              </StatusMessage>
            ) : isFirstLoad ? (
              <StatusMessage title="Searching the archive…" />
            ) : searchQuery.isError ? (
              <QueryError
                error={searchQuery.error}
                onRetry={() => void searchQuery.refetch()}
                retrying={searchQuery.isFetching}
              />
            ) : results.length === 0 ? (
              <StatusMessage tone="empty" title="No records match this search">
                <p>
                  {page > 1
                    ? 'There are no further records on this page. Go back a page, or widen your search.'
                    : 'No published disclosure matches these filters. Try more states, a shorter name, or a wider date range.'}
                </p>
              </StatusMessage>
            ) : (
              <>
                <div
                  className={`table-wrap${isRefetching ? ' is-refetching' : ''}`}
                  aria-busy={isRefetching || undefined}
                >
                  <table>
                    <caption className="visually-hidden">
                      Disclosure records, sorted alphabetically by purchaser name
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Purchaser</th>
                        <th scope="col">Committee ID</th>
                        <th scope="col">Candidate / subject</th>
                        <th scope="col">Position</th>
                        <th scope="col">Ad type</th>
                        <th scope="col">Matched states</th>
                        <th scope="col">Published</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((record) => (
                        <tr key={record.id}>
                          <th scope="row">
                            <Link to={recordHref(record.id)} viewTransition>
                              {record.purchaser_name}
                            </Link>
                          </th>
                          {/* committee_id uses omitempty: the key is absent, not empty. */}
                          <td>{record.committee_id ?? NOT_AVAILABLE}</td>
                          <td>{record.subject_name}</td>
                          <td>{positionLabel(record.position)}</td>
                          <td>{adTypeLabel(record.ad_type)}</td>
                          {/* states can be null if the state lookup failed — treat as empty. */}
                          <td>{record.states?.length ? record.states.join(', ') : NOT_AVAILABLE}</td>
                          <td>
                            <time dateTime={isoDateTime(record.published_at)}>
                              {formatDate(record.published_at)}
                            </time>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* `states` on a search result is scoped to the search, so it must not be
                    read as the ad's full footprint. */}
                <p className="table-note">
                  <strong>Matched states</strong> lists only the states you searched in which this
                  advertisement ran. It is not the advertisement’s full geographic footprint — open
                  a record to see every state it was published for. Results are sorted
                  alphabetically by purchaser name.
                </p>

                <nav className="pagination" aria-label="Result pages">
                  <button
                    type="button"
                    className="button"
                    disabled={page <= 1}
                    onClick={() => updateParams({ page: page - 1 <= 1 ? null : page - 1 })}
                  >
                    ←
                  </button>
                  <span className="pagination-position">Page {page}</span>
                  <button
                    type="button"
                    className="button"
                    disabled={!mayHaveNextPage || atPageCap}
                    onClick={() => updateParams({ page: page + 1 })}
                  >
                    →
                  </button>
                </nav>

                {/* The 200-page cap is deliberate; say so rather than silently stopping. */}
                {atPageCap ? (
                  <StatusMessage tone="empty" title="This is the last page the archive will serve">
                    <p>
                      A single search can be paged {SEARCH_LIMITS.maxPage} pages deep. To reach
                      records beyond this point, narrow your search — add a purchaser or candidate
                      name, a date range, or fewer states.
                    </p>
                  </StatusMessage>
                ) : null}
              </>
            )}
          </section>
        </>
      )}
    </div>
  )
}
